import { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from './api';
import { useFilePreview, FilePreviewModal } from './FilePreview';

// ─── Constants ────────────────────────────────────────────────────────────────

export const EQUIPMENT_CATEGORIES = ['LED', 'Video', 'Sound', 'Light', 'Rigging', 'Cables', 'PDU', 'Accessories', 'Other'];
const STANDARD_CATEGORIES = EQUIPMENT_CATEGORIES.filter(c => c !== 'Other');

const catConfig = {
  LED:         { color: 'var(--accent)', bg: '#2d0a0d' },
  Video:       { color: 'var(--info-text)', bg: 'var(--info-bg)' },
  Sound:       { color: 'var(--success-text)', bg: 'var(--success-bg)' },
  Light:       { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  Rigging:     { color: 'var(--purple-text)', bg: 'var(--purple-bg)' },
  Cables:      { color: '#14b8a6', bg: '#042f2a' },
  PDU:         { color: '#f97316', bg: '#2d1502' },
  Accessories: { color: '#ec4899', bg: '#2d0a1c' },
  Other:       { color: 'var(--text-muted)',    bg: 'var(--bg-subtle)' },
};

/** Масив категорій позиції (з fallback на стару одиночну). */
export const getCats = e => (Array.isArray(e?.categories) && e.categories.length)
  ? e.categories
  : (e?.category ? [e.category] : []);

/** Кастомна категорія — та, якої немає у стандартному списку. */
const isCustomCat = c => !STANDARD_CATEGORIES.includes(c);

export const EQUIPMENT_UNITS = ['pcs', 'sqm', 'set'];
export const unitOf = e => e?.unit || 'pcs';
export const serviceQty = e => Math.max(0, Number(e?.requireService) || 0);

// Статуси проєктів → стан резервування обладнання:
//   Active / Confirmed  → reserved (недоступне для планування)
//   Planning / On Hold  → planned  (доступне, але з міткою)
//   Completed / Canceled → не враховується (обладнання вільне)
const RESERVED_STATUSES = ['Active', 'Confirmed'];
const PLANNED_STATUSES  = ['Planning', 'On Hold'];

// ─── Availability engine ─────────────────────────────────────────────────────
// Рахує використання обладнання з проєктів. checkDate ('YYYY-MM-DD' | null):
// якщо задано — враховуються лише проєкти, чиї дати покривають цю дату
// (проєкт без дат враховується завжди).

export function computeEquipmentUsage(projects, checkDate = null) {
  const map = {}; // equipmentId -> { reserved, planned, usages: [] }

  for (const p of projects || []) {
    if (p.archived) continue;
    const isReserved = RESERVED_STATUSES.includes(p.status);
    const isPlanned  = PLANNED_STATUSES.includes(p.status);
    if (!isReserved && !isPlanned) continue;              // Completed / Canceled
    if (!Array.isArray(p.equipment) || !p.equipment.length) continue;

    if (checkDate) {
      const s = p.startDate || null;
      const e = p.endDate   || null;
      if (s && checkDate < s) continue;
      if (e && checkDate > e) continue;
    }

    for (const it of p.equipment) {
      if (!it.equipmentId) continue;                       // ручні позиції не резервують склад
      const qty = Math.max(0, parseInt(it.qty, 10) || 0);
      if (!qty) continue;
      if (!map[it.equipmentId]) map[it.equipmentId] = { reserved: 0, planned: 0, usages: [] };
      const m = map[it.equipmentId];
      if (isReserved) m.reserved += qty; else m.planned += qty;
      m.usages.push({
        projectId: p.id, projectName: p.name, status: p.status,
        qty, startDate: p.startDate || '', endDate: p.endDate || '',
        location: p.location || '', company: p.company || '',
        kind: isReserved ? 'reserved' : 'planned',
      });
    }
  }
  return map;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const thStyle = {
  padding: '10px 14px', textAlign: 'left', color: 'var(--text-dim)',
  fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase',
  letterSpacing: '1px', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap', background: 'var(--bg-card)',
};
const tdStyle = {
  padding: '11px 14px', color: 'var(--text-body)', fontSize: '13px',
  borderBottom: '1px solid var(--bg-hover)', verticalAlign: 'middle', whiteSpace: 'nowrap',
};
const inputStyle = {
  background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: '6px',
  color: 'var(--text-body)', padding: '8px 12px', fontSize: '13px', width: '100%',
  boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' };
const btnPrimary = { background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' };
const btnGhost   = { background: 'transparent', border: '1px solid var(--text-disabled)', color: 'var(--text-secondary)', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' };

function CategoryBadge({ category }) {
  const cfg = catConfig[category] || catConfig.Other;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 10px', fontSize: '11px', fontWeight: 'bold' }}>
      {category}
    </span>
  );
}

function CategoryBadges({ item }) {
  const cats = getCats(item);
  if (!cats.length) return <CategoryBadge category="Other" />;
  return (
    <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
      {cats.map(c => <CategoryBadge key={c} category={c} />)}
    </span>
  );
}

function StatusChip({ status }) {
  const reserved = RESERVED_STATUSES.includes(status);
  const color = reserved ? 'var(--accent)' : 'var(--warning-text)';
  return (
    <span style={{ color, border: `1px solid ${color}`, borderRadius: '4px', padding: '1px 8px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}>
      {status}
    </span>
  );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────

function Modal({ children, maxWidth = 480 }) {
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: `${maxWidth}px`, maxHeight: '90vh', overflow: 'auto', fontFamily: 'Arial, sans-serif' }}>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ─── Equipment form (add / edit) ──────────────────────────────────────────────

function EquipmentForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(() => {
    if (!initial) return { name: '', quantity: 1, requireService: 0, unit: 'pcs', notes: '' };
    return { unit: 'pcs', requireService: 0, ...initial };
  });
  // Мультивибір категорій: стандартні — чекбокси-кнопки, Other — своє значення
  const initCats = initial ? getCats(initial) : ['LED'];
  const [cats, setCatsState]  = useState(() => initCats.filter(c => !isCustomCat(c)));
  const [customOn, setCustomOn] = useState(() => initCats.some(isCustomCat));
  const [customVal, setCustomVal] = useState(() => initCats.filter(isCustomCat).join(', '));

  // Файли, що завантажаться одразу після збереження позиції
  const [pendingFiles, setPendingFiles] = useState([]);
  const fileInput = useRef(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleCat = c => setCatsState(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  const addFiles = e => {
    // ВАЖЛИВО: зчитати FileList синхронно, ДО очищення input.value —
    // інакше React виконає updater пізніше, коли список уже порожній.
    const files = Array.from(e.target.files || []);
    if (files.length) setPendingFiles(prev => [...prev, ...files]);
    if (fileInput.current) fileInput.current.value = '';
  };
  const removeFile = idx => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const custom = customOn
      ? customVal.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    const categories = [...cats, ...custom];
    onSave({
      ...form,
      categories: categories.length ? categories : ['Other'],
      quantity: Math.max(0, parseInt(form.quantity, 10) || 0),
      requireService: Math.max(0, parseInt(form.requireService, 10) || 0),
      id: initial?.id,
    }, pendingFiles);
  };

  return (
    <Modal>
      <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '18px' }}>{initial ? 'Edit Equipment' : 'Add Equipment'}</h2>
        <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: '0 28px 28px' }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Name *</label>
          <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. LED Panel P3.9 500x500" required />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Categories (можна кілька)</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {STANDARD_CATEGORIES.map(c => {
              const on = cats.includes(c);
              return (
                <button
                  key={c} type="button"
                  onClick={() => toggleCat(c)}
                  style={{
                    padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                    border: `1px solid ${on ? catConfig[c].color : 'var(--border-strong)'}`,
                    background: on ? catConfig[c].bg : 'transparent',
                    color: on ? catConfig[c].color : 'var(--text-dim)',
                    fontWeight: on ? 'bold' : 'normal',
                  }}>
                  {on ? '✓ ' : ''}{c}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setCustomOn(v => !v)}
              style={{
                padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
                border: `1px solid ${customOn ? catConfig.Other.color : 'var(--border-strong)'}`,
                background: customOn ? catConfig.Other.bg : 'transparent',
                color: customOn ? 'var(--text-body)' : 'var(--text-dim)',
                fontWeight: customOn ? 'bold' : 'normal',
              }}>
              {customOn ? '✓ ' : ''}Other
            </button>
          </div>
          {customOn && (
            <input
              style={{ ...inputStyle, marginTop: '8px' }}
              placeholder="Своя категорія (кілька — через кому)"
              value={customVal}
              onChange={e => setCustomVal(e.target.value)}
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 1fr', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Quantity in stock</label>
            <input style={inputStyle} type="number" min="0" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Unit</label>
            <select style={inputStyle} value={form.unit} onChange={e => set('unit', e.target.value)}>
              {EQUIPMENT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label style={{ ...labelStyle, color: 'var(--accent)' }}>Require service</label>
            <input
              style={{ ...inputStyle, borderColor: (parseInt(form.requireService, 10) || 0) > 0 ? 'var(--accent)' : 'var(--border-strong)' }}
              type="number" min="0"
              value={form.requireService}
              onChange={e => set('requireService', e.target.value)}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Notes</label>
          <textarea style={{ ...inputStyle, height: '64px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Specs, condition, location..." />
        </div>

        {/* Files & photos — завантажаться одразу після збереження */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Files & Photos {pendingFiles.length > 0 && `(${pendingFiles.length})`}</label>
            <button type="button" onClick={() => fileInput.current?.click()}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
              + Attach
            </button>
            <input ref={fileInput} type="file" multiple style={{ display: 'none' }} onChange={addFiles}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" />
          </div>
          {pendingFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {pendingFiles.map((f, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  {f.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(f)} alt={f.name}
                      style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)', display: 'block' }} />
                  ) : (
                    <div style={{ width: '72px', height: '72px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <span>📄</span>
                      <span style={{ color: 'var(--text-faint)', fontSize: '8px', padding: '0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '64px' }}>{f.name}</span>
                    </div>
                  )}
                  <span onClick={() => removeFile(idx)}
                    style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(0,0,0,0.7)', color: 'var(--accent)', borderRadius: '4px', padding: '0 5px', fontSize: '12px', cursor: 'pointer' }}>
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={btnGhost}>Cancel</button>
          <button type="submit" style={btnPrimary}>{initial ? 'Save Changes' : 'Add Equipment'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Add to project modal ─────────────────────────────────────────────────────

function AddToProjectModal({ item, projects, usage, onConfirm, onCancel }) {
  const dryHire = (projects || []).filter(p =>
    !p.archived && p.type === 'Dry hire' && !['Completed', 'Canceled'].includes(p.status)
  );
  const [projectId, setProjectId] = useState('');
  const [qty, setQty] = useState(1);

  const availableNow = item.quantity - (usage?.reserved || 0) - serviceQty(item);

  const submit = () => {
    const p = dryHire.find(x => String(x.id) === String(projectId));
    if (!p || !(parseInt(qty, 10) > 0)) return;
    onConfirm(p, parseInt(qty, 10));
  };

  return (
    <Modal>
      <div style={{ padding: '24px 28px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '18px' }}>Add to project</h2>
          <button onClick={onCancel} style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '16px' }}>
          {item.name} — available now: <b style={{ color: availableNow > 0 ? 'var(--success-text)' : 'var(--accent)' }}>{availableNow}</b> of {item.quantity} {unitOf(item)}
        </div>

        {dryHire.length === 0 ? (
          <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>
            Немає активних проєктів типу Dry hire. Створи проєкт з цим типом — і він зʼявиться тут.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Project (Dry hire)</label>
              <select style={inputStyle} value={projectId} onChange={e => setProjectId(e.target.value)}>
                <option value="">Select project...</option>
                {dryHire.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} [{p.status}]{p.startDate ? ` ${p.startDate} → ${p.endDate || '…'}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '24px', maxWidth: '140px' }}>
              <label style={labelStyle}>Quantity, {unitOf(item)}</label>
              <input style={inputStyle} type="number" min="1" value={qty} onChange={e => setQty(e.target.value)} />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnGhost}>Cancel</button>
          {dryHire.length > 0 && (
            <button onClick={submit} disabled={!projectId} style={{ ...btnPrimary, opacity: projectId ? 1 : 0.5 }}>Add</button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Delete confirm ───────────────────────────────────────────────────────────

function DeleteModal({ item, onConfirm, onCancel }) {
  return (
    <Modal maxWidth={400}>
      <div style={{ padding: '28px' }}>
        <h3 style={{ color: 'var(--text-primary)', margin: '0 0 12px' }}>Delete equipment?</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: '0 0 24px' }}>
          «{item.name}» та всі його файли буде видалено назавжди.
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnGhost}>Cancel</button>
          <button onClick={onConfirm} style={btnPrimary}>Delete</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Files section (side panel) ───────────────────────────────────────────────

function FilesSection({ item }) {
  const [files, setFiles]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const { previewFile, openPreview, closePreview } = useFilePreview();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.equipment.listFiles(item.id)
      .then(res => { if (alive) setFiles(res.files || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [item.id]);

  const handleUpload = async e => {
    const list = Array.from(e.target.files || []); // зчитати синхронно
    if (!list.length) return;
    if (item.id > 1e12) {
      alert('Ця позиція ще синхронізується з сервером. Закрий панель, зачекай секунду і спробуй знову.');
      if (fileInput.current) fileInput.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const res = await api.equipment.uploadFiles(item.id, list);
      setFiles(prev => [...(res.files || []), ...prev]);
    } catch (err) {
      alert('Не вдалося завантажити файли: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const handleDelete = async f => {
    try { await api.equipment.deleteFile(item.id, f.id); } catch {}
    setFiles(prev => prev.filter(x => x.id !== f.id));
  };

  const images = files.filter(f => f.isImage);
  const docs   = files.filter(f => !f.isImage);

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Files & Photos {files.length > 0 && `(${files.length})`}
        </div>
        <button onClick={() => fileInput.current?.click()} disabled={uploading}
          style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
          {uploading ? 'Uploading…' : '+ Upload'}
        </button>
        <input ref={fileInput} type="file" multiple style={{ display: 'none' }} onChange={handleUpload}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" />
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>Loading…</div>
      ) : files.length === 0 ? (
        <div style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>Немає файлів. Додай фото чи документи.</div>
      ) : (
        <>
          {images.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '10px' }}>
              {images.map(f => (
                <div key={f.id} style={{ position: 'relative' }}>
                  <img src={api.equipment.fileUrl(item.id, f.id)} alt={f.name}
                    onClick={() => openPreview({
                      url: api.equipment.fileUrl(item.id, f.id),
                      downloadUrl: api.equipment.fileUrl(item.id, f.id, { download: true }),
                      name: f.name,
                      mime: f.mime,
                    })}
                    style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border)', display: 'block', cursor: 'pointer' }} />
                  <span onClick={() => handleDelete(f)}
                    style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.7)', color: 'var(--accent)', borderRadius: '4px', padding: '0 6px', fontSize: '12px', cursor: 'pointer' }}>
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}
          {docs.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 12px', marginBottom: '6px' }}>
              <span>📄</span>
              <span
                onClick={() => openPreview({
                  url: api.equipment.fileUrl(item.id, f.id),
                  downloadUrl: api.equipment.fileUrl(item.id, f.id, { download: true }),
                  name: f.name,
                  mime: f.mime,
                })}
                style={{ color: 'var(--info-text)', fontSize: '12px', cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name}
              </span>
              <span style={{ color: 'var(--text-disabled)', fontSize: '10px' }}>{(f.size / 1024).toFixed(0)} KB</span>
              <span onClick={() => handleDelete(f)} style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: '13px' }}>×</span>
            </div>
          ))}
        </>
      )}
      <FilePreviewModal file={previewFile} onClose={closePreview} />
    </div>
  );
}

// ─── Side panel ───────────────────────────────────────────────────────────────

function SidePanel({ item, usage, onClose, onEdit, onDelete, onAddToProject }) {
  const reserved  = usage?.reserved || 0;
  const planned   = usage?.planned  || 0;
  const service   = serviceQty(item);
  const available = item.quantity - reserved - service;

  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1500 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', maxWidth: '92vw', background: 'var(--bg-panel)', borderLeft: '1px solid var(--border)', zIndex: 1600, overflow: 'auto', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '16px', marginBottom: '6px' }}>{item.name}</div>
            <CategoryBadges item={item} />
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', flex: 1 }}>

          {/* Stock summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '8px', marginBottom: '20px' }}>
            {[
              ['In stock',  item.quantity, 'var(--text-body)'],
              ['Reserved',  reserved,      reserved > 0 ? 'var(--accent)' : 'var(--text-disabled)'],
              ['Planned',   planned,       planned  > 0 ? 'var(--warning-text)' : 'var(--text-disabled)'],
              ['Require service', service, service > 0 ? 'var(--accent)' : 'var(--text-disabled)'],
              ['Available', available,     available > 0 ? 'var(--success-text)' : 'var(--accent)'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 6px', textAlign: 'center' }}>
                <div style={{ color: 'var(--text-dim)', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{label}</div>
                <div style={{ color, fontSize: '18px', fontWeight: 'bold' }}>{val}</div>
                <div style={{ color: 'var(--text-dim)', fontSize: '9px' }}>{unitOf(item)}</div>
              </div>
            ))}
          </div>

          {/* Usage on projects */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>On projects</div>
            {(usage?.usages || []).length === 0 ? (
              <div style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>Не використовується у проєктах</div>
            ) : (
              usage.usages.map((u, i) => (
                <div key={i} style={{ background: 'var(--bg-card)', border: `1px solid ${u.kind === 'reserved' ? '#3d1518' : 'var(--border)'}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 'bold' }}>{u.projectName}</span>
                    <StatusChip status={u.status} />
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    Qty: <b style={{ color: u.kind === 'reserved' ? 'var(--accent)' : 'var(--warning-text)' }}>{u.qty} {unitOf(item)}</b>
                    {u.startDate && <span style={{ marginLeft: '10px' }}>📅 {u.startDate} → {u.endDate || '…'}</span>}
                  </div>
                  {(u.location || u.company) && (
                    <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>
                      {u.location && <span>📍 {u.location}</span>}
                      {u.company && <span style={{ marginLeft: u.location ? '10px' : 0 }}>🏢 {u.company}</span>}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Files & photos */}
          <FilesSection item={item} />

          {/* Notes */}
          {item.notes && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ color: 'var(--text-dim)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Notes</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                {item.notes}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px' }}>
          <button onClick={onAddToProject} style={{ ...btnPrimary, flex: 1, padding: '10px' }}>+ Add to project</button>
          <button onClick={onEdit} style={{ ...btnGhost, padding: '10px 16px' }}>Edit</button>
          <button onClick={onDelete} style={{ ...btnGhost, padding: '10px 16px', color: 'var(--accent)', borderColor: '#3d1518' }}>Delete</button>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// Локальний (несинхронізований) id — це Date.now(), тобто > 10^12
const isLocalId = id => typeof id === 'number' && id > 1e12;

export default function EquipmentPage({ onBack, equipment, setEquipment, projects = [], setProjects }) {
  const [search, setSearch]             = useState('');
  const [catFilter, setCatFilter]       = useState('All');
  const [checkDate, setCheckDate]       = useState('');
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [formData, setFormData]         = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [panelItem, setPanelItem]       = useState(null);
  const [addTarget, setAddTarget]       = useState(null);

  const usageMap = useMemo(
    () => computeEquipmentUsage(projects, checkDate || null),
    [projects, checkDate]
  );

  // Автосинхронізація: позиції, збережені локально (коли сервер був недоступний),
  // досоздаються в БД, а тимчасові id замінюються на серверні — і в обладнанні,
  // і в проєктах, які на них посилаються.
  const syncingRef = useRef(false);
  useEffect(() => {
    const local = equipment.filter(e => isLocalId(e.id));
    if (!local.length || syncingRef.current) return;
    syncingRef.current = true;
    (async () => {
      for (const item of local) {
        try {
          const res = await api.equipment.create({ ...item, id: undefined });
          const saved = res.equipment;
          setEquipment(prev => prev.map(e => e.id === item.id ? saved : e));
          if (setProjects) {
            setProjects(prev => prev.map(p => {
              if (!Array.isArray(p.equipment) || !p.equipment.some(it => it.equipmentId === item.id)) return p;
              return { ...p, equipment: p.equipment.map(it => it.equipmentId === item.id ? { ...it, equipmentId: saved.id } : it) };
            }));
          }
          setPanelItem(prev => (prev?.id === item.id ? saved : prev));
        } catch (err) {
          console.warn('Equipment sync failed for', item.name, err);
        }
      }
      syncingRef.current = false;
    })();
  }, [equipment, setEquipment, setProjects]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return equipment.filter(e => {
      if (catFilter !== 'All') {
        const cs = getCats(e);
        // Фільтр Other показує і кастомні категорії
        const match = catFilter === 'Other'
          ? cs.some(isCustomCat) || cs.includes('Other')
          : cs.includes(catFilter);
        if (!match) return false;
      }
      const u = usageMap[e.id];
      if (onlyAvailable && (e.quantity - (u?.reserved || 0) - serviceQty(e)) <= 0) return false;
      if (!q) return true;
      // Пошук по назві/нотатках + по локації, клієнту і назві проєкту, де використовується
      const own = `${e.name} ${e.notes}`.toLowerCase().includes(q);
      const inUse = (u?.usages || []).some(x =>
        `${x.location} ${x.company} ${x.projectName}`.toLowerCase().includes(q)
      );
      return own || inUse;
    });
  }, [equipment, search, catFilter, usageMap, onlyAvailable]);

  const handleSave = async (data, pendingFiles = []) => {
    try {
      const isNew = !data.id;
      const res = isNew
        ? await api.equipment.create(data)
        : await api.equipment.update(data.id, data);
      const saved = res.equipment;
      setEquipment(prev => {
        const exists = prev.find(e => e.id === saved.id);
        return exists ? prev.map(e => e.id === saved.id ? saved : e) : [saved, ...prev];
      });
      if (panelItem?.id === saved.id) setPanelItem(saved);
      // Прикріплені у формі файли — одразу на сервер
      if (pendingFiles.length) {
        try {
          await api.equipment.uploadFiles(saved.id, pendingFiles);
        } catch (upErr) {
          alert('Позицію збережено, але файли не завантажились: ' + upErr.message);
        }
      }
    } catch (err) {
      console.warn('API save error, falling back to local:', err);
      const fallback = { ...data, id: data.id || Date.now() };
      setEquipment(prev => {
        const exists = prev.find(e => e.id === fallback.id);
        return exists ? prev.map(e => e.id === fallback.id ? fallback : e) : [fallback, ...prev];
      });
      if (pendingFiles.length) alert('Сервер недоступний — файли не завантажено, спробуй пізніше через панель позиції.');
    }
    setFormData(null);
  };

  const handleDelete = async () => {
    try { await api.equipment.delete(deleteTarget.id); } catch (e) { console.warn('API delete error:', e); }
    setEquipment(prev => prev.filter(x => x.id !== deleteTarget.id));
    if (panelItem?.id === deleteTarget.id) setPanelItem(null);
    setDeleteTarget(null);
  };

  // Додати позицію до проєкту Dry hire прямо зі сторінки Equipment
  const handleAddToProject = async (project, qty) => {
    const eq = Array.isArray(project.equipment) ? [...project.equipment] : [];
    const idx = eq.findIndex(it => it.equipmentId === addTarget.id);
    if (idx >= 0) eq[idx] = { ...eq[idx], qty: (parseInt(eq[idx].qty, 10) || 0) + qty };
    else eq.push({ equipmentId: addTarget.id, name: addTarget.name, category: getCats(addTarget).join(' / '), unit: unitOf(addTarget), qty });

    const updated = { ...project, equipment: eq };
    try {
      const res = await api.projects.update(project.id, updated);
      const saved = res.project;
      if (setProjects) setProjects(prev => prev.map(p => p.id === saved.id ? saved : p));
    } catch (err) {
      console.warn('API save error, falling back to local:', err);
      if (setProjects) setProjects(prev => prev.map(p => p.id === project.id ? updated : p));
    }
    setAddTarget(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>EQUIPMENT</span>
          <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{equipment.length} total</span>
        </div>
        <button onClick={() => setFormData({})} style={{ ...btnPrimary, padding: '9px 20px', fontSize: '13px' }}>
          + Add Equipment
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          <input
            style={{ ...inputStyle, maxWidth: '240px' }}
            placeholder="Search: name, location, client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Availability on:</span>
            <input
              style={{ ...inputStyle, width: '150px', colorScheme: 'var(--color-scheme)' }}
              type="date"
              value={checkDate}
              onChange={e => setCheckDate(e.target.value)}
            />
            {checkDate && (
              <span onClick={() => setCheckDate('')} style={{ color: 'var(--accent)', cursor: 'pointer', fontSize: '13px' }}>×</span>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyAvailable} onChange={e => setOnlyAvailable(e.target.checked)} />
            Only available
          </label>
          <span style={{ color: 'var(--text-disabled)', fontSize: '12px', marginLeft: 'auto' }}>{filtered.length} of {equipment.length}</span>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {['All', ...EQUIPMENT_CATEGORIES].map(c => (
            <button
              key={c}
              onClick={() => setCatFilter(c)}
              style={{
                padding: '6px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                border: `1px solid ${catFilter === c ? 'var(--accent)' : 'var(--border-strong)'}`,
                background: 'transparent',
                color: catFilter === c ? 'var(--accent)' : 'var(--text-dim)',
                fontWeight: catFilter === c ? 'bold' : 'normal',
              }}>
              {c}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={{ background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>In stock</th>
                <th style={thStyle}>Reserved</th>
                <th style={thStyle}>Planned</th>
                <th style={{ ...thStyle, color: 'var(--accent)' }}>Require service</th>
                <th style={thStyle}>Available{checkDate ? ` (${checkDate})` : ''}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-disabled)', padding: '48px' }}>
                    No equipment found
                  </td>
                </tr>
              ) : filtered.map(e => {
                const u = usageMap[e.id];
                const reserved  = u?.reserved || 0;
                const planned   = u?.planned  || 0;
                const service   = serviceQty(e);
                const available = e.quantity - reserved - service;
                const isActive  = panelItem?.id === e.id;
                return (
                  <tr
                    key={e.id}
                    onClick={() => setPanelItem(isActive ? null : e)}
                    style={{ cursor: 'pointer', background: isActive ? 'var(--bg-hover)' : 'transparent', borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent' }}
                    onMouseEnter={ev => { if (!isActive) ev.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={ev => { if (!isActive) ev.currentTarget.style.background = 'transparent'; }}>
                    <td style={tdStyle}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '14px' }}>{e.name}</div>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'normal' }}><CategoryBadges item={e} /></td>
                    <td style={tdStyle}>{e.quantity} <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>{unitOf(e)}</span></td>
                    <td style={tdStyle}>
                      {reserved > 0 ? <span style={{ color: 'var(--accent)', fontWeight: 'bold' }}>{reserved} <span style={{ fontWeight: 'normal', fontSize: '11px' }}>{unitOf(e)}</span></span> : <span style={{ color: 'var(--border-strong)' }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      {planned > 0 ? <span style={{ color: 'var(--warning-text)', fontWeight: 'bold' }} title="Заплановано (Planning / On Hold) — доступне для інших проєктів">{planned} <span style={{ fontWeight: 'normal', fontSize: '11px' }}>{unitOf(e)}</span> ◷</span> : <span style={{ color: 'var(--border-strong)' }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      {service > 0 ? <span style={{ color: 'var(--accent)', fontWeight: 'bold' }} title="У сервісі — недоступне для замовлення">🔧 {service} <span style={{ fontWeight: 'normal', fontSize: '11px' }}>{unitOf(e)}</span></span> : <span style={{ color: 'var(--border-strong)' }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ color: available > 0 ? 'var(--success-text)' : 'var(--accent)', fontWeight: 'bold' }}>{available} <span style={{ fontWeight: 'normal', fontSize: '11px' }}>{unitOf(e)}</span></span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ color: 'var(--text-disabled)', fontSize: '11px', marginTop: '10px' }}>
          Reserved — на проєктах Active/Confirmed (недоступне). Planned ◷ — на проєктах Planning/On Hold (доступне для планування, з міткою).
          Require service 🔧 — у сервісі, недоступне для замовлення. Completed і Canceled проєкти обладнання не тримають.
        </div>
      </div>

      {/* Side panel */}
      {panelItem && (
        <SidePanel
          item={panelItem}
          usage={usageMap[panelItem.id]}
          onClose={() => setPanelItem(null)}
          onEdit={() => setFormData(panelItem)}
          onDelete={() => setDeleteTarget(panelItem)}
          onAddToProject={() => setAddTarget(panelItem)}
        />
      )}

      {/* Modals */}
      {formData !== null && (
        <EquipmentForm
          initial={formData.id ? formData : null}
          onSave={handleSave}
          onCancel={() => setFormData(null)}
        />
      )}
      {deleteTarget && (
        <DeleteModal item={deleteTarget} onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
      {addTarget && (
        <AddToProjectModal
          item={addTarget}
          usage={usageMap[addTarget.id]}
          projects={projects}
          onConfirm={handleAddToProject}
          onCancel={() => setAddTarget(null)}
        />
      )}
    </div>
  );
}
